import React, { FC, useCallback, useState } from 'react';
import { DevicesAPI } from 'api/DevicesAPI';
import { useQuery, useQueryCache } from 'react-query';
import { QueryKey } from 'api/queryKey';
import { Input, Layout, PageHeader, Space } from 'antd';
import { Content } from 'common/StyledComponents';
import { toSimpleDate } from 'common/dateFnsUtils';
import {
  commonPaginationConfig,
  initialPageParams,
  PageParams,
} from 'models/page';
import { atom, useAtom } from 'jotai';
import { Link } from 'react-router-dom';
import { NewDeviceRoute } from 'routes';
import Column from 'antd/lib/table/Column';
import { CloseOutlined } from '@ant-design/icons/lib';
import { FormattedMessage } from 'react-intl';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { NumberedTable } from 'common/components/NumberedTable';
import { DeleteDeviceButton } from 'pages/SuperAdminModule/DevicesManagement/components/DeleteDeviceButton';
import { HigoDevice } from 'models/higoDevice';

const devicesFiltersAtom = atom<PageParams>(initialPageParams);
const { Search } = Input;

const DevicesPage: FC = () => {
  const queryCache = useQueryCache();
  const [pageMetaData, setPageMetaData] = useAtom(devicesFiltersAtom);
  const [filters, setFilters] = useState({});
  const intlMessage = useFormatMessage();

  const { isFetching, data } = useQuery(
    [QueryKey.DevicesList, pageMetaData, filters],
    () => DevicesAPI.fetch({ ...pageMetaData, ...filters }),
    {
      keepPreviousData: true,
    }
  );

  const invalidateDevicesList = useCallback(
    () => queryCache.invalidateQueries(QueryKey.DevicesList),
    [queryCache]
  );

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  return (
    <Layout>
      <Content>
        <PageHeader
          title={intlMessage({ id: 'devices' })}
          extra={[
            <Link
              className="ant-btn ant-btn-primary"
              key={NewDeviceRoute}
              to={NewDeviceRoute}
            >
              <FormattedMessage id="devices.addNew" />
            </Link>,
          ]}
        />
        <Search
          placeholder={intlMessage({ id: 'devices.serialNumber.placeholder' })}
          onSearch={(value) => {
            setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
            setFilters((filters) => ({
              ...filters,
              serialNumber: value,
              boxSerialNumber: value,
            }));
          }}
          style={{ width: 450, marginBottom: '1rem' }}
          allowClear
        />
        <NumberedTable
          rowKey="id"
          loading={isFetching}
          dataSource={data?.content}
          pagination={paginationConfig}
          scroll={{ x: 'auto' }}
        >
          <Column
            title={intlMessage({ id: 'deviceSerialNumber' })}
            dataIndex="serialNumber"
            render={(value) => (value !== null && value !== '' ? value : '--')}
          />
          <Column
            title={intlMessage({ id: 'devices.boxSerialNumber' })}
            dataIndex="boxSerialNumber"
            render={(value) => (value !== null && value !== '' ? value : '--')}
          />
          <Column
            title={intlMessage({ id: 'registrationDate' })}
            dataIndex="createDate"
            align="center"
            render={(value) => toSimpleDate(value)}
          />

          <Column
            title={intlMessage({ id: 'devices.firmware' })}
            dataIndex="firmwareVersion"
            align="center"
            render={(value) => (value !== null && value !== '' ? value : '--')}
          />
          <Column
            title={intlMessage({ id: 'devices.lastConnection' })}
            dataIndex="lastMessageTime"
            align="center"
            render={(value) => toSimpleDate(value)}
          />
          <Column
            title={intlMessage({ id: 'partner' })}
            dataIndex={['businessPartner', 'name']}
            align="center"
            render={(value) => value || <CloseOutlined />}
          />
          <Column
            key="actions"
            title={intlMessage({ id: 'actions' })}
            align="right"
            render={(_, record: HigoDevice) => (
              <Space direction={'vertical'} style={{ width: '150px' }}>
                <DeleteDeviceButton
                  danger
                  device={record}
                  onDelete={invalidateDevicesList}
                />
              </Space>
            )}
          />
        </NumberedTable>
      </Content>
    </Layout>
  );
};

// eslint-disable-next-line import/no-default-export
export default DevicesPage;
